import type { CSSProperties } from 'react';
import { categoryHue } from '@/shared/lib/category-color';

/**
 * An appointment profile's colour arrives from data (the profile record), so it is a runtime
 * value. It is handed to the element as two custom properties that token-bearing classes read:
 * `--pa-profile-color` is the colour itself (`bg-[var(--pa-profile-color)]`,
 * `text-[var(--pa-profile-color)]`), and `--pa-profile-tint` is a 10% tint of it over
 * transparent (`bg-[var(--pa-profile-tint)]`, the icon-badge fill). The tint is composed here
 * rather than inside a class so every class stays a bare `var()` read — the only arbitrary shape
 * `scripts/arbitrary-class-ledger.json` accepts.
 */
export function profileColorVars(color: string): CSSProperties {
  // Drawn on the muted Okabe-Ito ramp (Antonin, 13 Oct): the stored colour's nearest hue, 500 for the
  // colour itself and its 100 tint; a grey keeps the stored colour and its 10% mix.
  const hue = categoryHue(color);
  return {
    '--pa-profile-color': hue ? `var(--cat-${hue}-500)` : color,
    '--pa-profile-tint': hue ? `var(--cat-${hue}-100)` : `color-mix(in srgb, ${color} 10%, transparent)`,
  } as CSSProperties;
}
